import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface DisplaceParams {
  horizontalScale: number;
  verticalScale: number;
  undefinedAreas: "stretch" | "wrap";
}

export const Displace: Filter<DisplaceParams> = {
  id: "displace",
  displayName: "Displace",
  defaultParams: { horizontalScale: 10, verticalScale: 10, undefinedAreas: "stretch" },

  apply(input: PixelBuffer, params: DisplaceParams, selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) {
      dst.set(src);
      return out;
    }

    const hScale = params.horizontalScale;
    const vScale = params.verticalScale;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (selection && selection.contains(x, y) === 0) {
          const o = (y * w + x) * 4;
          dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
          continue;
        }

        const dx = (src[(y * w + x) * 4]! - 128) * hScale / 128;
        const dy = (src[(y * w + x) * 4 + 1]! - 128) * vScale / 128;
        let sx = x + dx, sy = y + dy;

        if (params.undefinedAreas === "wrap") {
          sx = ((sx % w) + w) % w;
          sy = ((sy % h) + h) % h;
        } else {
          sx = Math.max(0, Math.min(w - 1, sx));
          sy = Math.max(0, Math.min(h - 1, sy));
        }

        const ix = Math.floor(sx), iy = Math.floor(sy);
        const fx = sx - ix, fy = sy - iy;
        const x0 = Math.max(0, Math.min(w - 1, ix));
        const y0 = Math.max(0, Math.min(h - 1, iy));
        const x1 = Math.max(0, Math.min(w - 1, ix + 1));
        const y1 = Math.max(0, Math.min(h - 1, iy + 1));

        const o = (y * w + x) * 4;
        for (let c = 0; c < 4; c++) {
          const v00 = src[(y0 * w + x0) * 4 + c]!;
          const v10 = src[(y0 * w + x1) * 4 + c]!;
          const v01 = src[(y1 * w + x0) * 4 + c]!;
          const v11 = src[(y1 * w + x1) * 4 + c]!;
          dst[o + c] = Math.round(v00 * (1 - fx) * (1 - fy) + v10 * fx * (1 - fy) + v01 * (1 - fx) * fy + v11 * fx * fy);
        }
      }
    }
    return out;
  },
};
